import { getUserMeasurementPreferenceFromState } from "@/redux/selectors"
import { useRootSelector } from "@/redux/useRootSelector"
import i18n from "@/translations/i18n"

import type { MeasurementPreferenceProps } from "./sensor-conversions"
import type { AnySensorKey } from "./sensors"
import type { LabelLength } from "./string-formatting"

// TODO: Could definitely organize this better
export function getSensorUnitLabel({
  fieldName,
  labelLength = "short",
  measurementPreference,
}: MeasurementPreferenceProps & {
  fieldName: AnySensorKey
  labelLength?: LabelLength
}): string {
  const isMetric = measurementPreference === "metric"
  switch (fieldName) {
    case "altitude": {
      return isMetric
        ? i18n.t("sensorFields:altitude.unitLabel.metric")
        : i18n.t("sensorFields:altitude.unitLabel.us")
    }
    case "applicationRateReportsMm": {
      return isMetric
        ? i18n.t("sensorFields:applicationRateReportsMm.unitLabel.metric")
        : i18n.t("sensorFields:applicationRateReportsMm.unitLabel.us")
    }
    case "diameterMm": {
      return isMetric
        ? i18n.t("sensorFields:diameterMm.unitLabel.metric")
        : i18n.t("sensorFields:diameterMm.unitLabel.us")
    }
    case "distanceObservedMm": {
      return isMetric
        ? i18n.t("sensorFields:distanceObservedMm.unitLabel.metric")
        : i18n.t("sensorFields:distanceObservedMm.unitLabel.us")
    }
    case "flowRateEstimatedLpm": {
      return isMetric
        ? i18n.t("sensorFields:flowRateEstimatedLpm.unitLabel.metric")
        : i18n.t("sensorFields:flowRateEstimatedLpm.unitLabel.us")
    }
    case "measuredM": {
      return isMetric
        ? i18n.t("sensorFields:measuredM.unitLabel.metric")
        : i18n.t("sensorFields:measuredM.unitLabel.us")
    }
    case "heading": {
      return i18n.t("sensorFields:heading.unitLabel")
    }
    case "hoseDiameterMm": {
      return isMetric
        ? i18n.t("sensorFields:hoseDiameterMm.unitLabel.metric")
        : i18n.t("sensorFields:hoseDiameterMm.unitLabel.us")
    }
    case "hoseWheelMmPerMagnet": {
      return isMetric
        ? i18n.t("sensorFields:hoseWheelMmPerMagnet.unitLabel.metric")
        : i18n.t("sensorFields:hoseWheelMmPerMagnet.unitLabel.us")
    }
    case "internalSoc": {
      return i18n.t("sensorFields:internalSoc.unitLabel")
    }
    case "speedMillirpm":
    case "milliRpmAvg":
    case "milliRpmFast":
    case "milliRpmSlow": {
      return i18n.t("sensorFields:milliRpmFast.unitLabel")
    }
    case "normalThresholdLpm":{
      return isMetric
        ? i18n.t(`sensorFields:normalThresholdLpm.unitLabel.metric.${labelLength}`)
        : i18n.t("sensorFields:normalThresholdLpm.unitLabel.us")
    }
    case "flowRateOverrideLpm": {
      return isMetric
        ? i18n.t(`sensorFields:flowRateOverrideLpm.unitLabel.metric.${labelLength}`)
        : i18n.t(`sensorFields:flowRateOverrideLpm.unitLabel.us.${labelLength}`)
    }
    case "overThresholdLpm": {
      return isMetric
        ? i18n.t(`sensorFields:overThresholdLpm.unitLabel.metric.${labelLength}`)
        : i18n.t("sensorFields:overThresholdLpm.unitLabel.us")
    }
    case "mlPerPulse": {
      return isMetric
        ? i18n.t(`sensorFields:mlPerPulse.unitLabel.metric.${labelLength}`)
        : i18n.t("sensorFields:mlPerPulse.unitLabel.us")
    }
    case "nozzleDiameterMm": {
      return isMetric
        ? i18n.t(
            `sensorFields:nozzleDiameterMm.unitLabel.metric.${labelLength}`,
          )
        : i18n.t(`sensorFields:nozzleDiameterMm.unitLabel.us.${labelLength}`)
    }
    case "outerHoseWrapRadiusMm": {
      return isMetric
        ? i18n.t("sensorFields:outerHoseWrapRadiusMm.unitLabel.metric")
        : i18n.t("sensorFields:outerHoseWrapRadiusMm.unitLabel.us")
    }
    case "pivotRadiusMeters": {
      return isMetric
        ? i18n.t("sensorFields:pivotRadiusMeters.unitLabel.metric")
        : i18n.t("sensorFields:pivotRadiusMeters.unitLabel.us")
    }
    case "pressureObservedKpa":
    case "pressureReportsKpa":
    case "readingKpa":
    case "threshold":
    case "thresholdPsiLower":
    case "thresholdPsiUpper": {
      return isMetric
        ? i18n.t("sensorFields:pressureObservedKpa.unitLabel.metric")
        : i18n.t("sensorFields:pressureObservedKpa.unitLabel.us")
    }
    case "rateLpmAvg":
    case "rateLpmMax":
    case "rateLpmMin": {
      return isMetric
        ? i18n.t("sensorFields:rateLpmAvg.unitLabel.metric")
        : i18n.t("sensorFields:rateLpmAvg.unitLabel.us")
    }
    case "readingCelsius": {
      return isMetric
        ? i18n.t("sensorFields:readingCelsius.unitLabel.metric")
        : i18n.t("sensorFields:readingCelsius.unitLabel.us")
    }
    case "runDistanceMmCurrent":
    case "runDistanceMmElapsed":
    case "runDistanceMmMax": {
      return isMetric
        ? i18n.t("sensorFields:runDistanceMmCurrent.unitLabel.metric")
        : i18n.t("sensorFields:runDistanceMmCurrent.unitLabel.us")
    }
    case "linearSpeedMmHMax":
    case "linearSpeedMmHMin": {
      return isMetric
        ? i18n.t("sensorFields:linearSpeedMmHMin.unitLabel.metric")
        : i18n.t("sensorFields:linearSpeedMmHMin.unitLabel.us")
    }
    case "runSpeedMmpm": {
      return isMetric
        ? i18n.t("sensorFields:runSpeedMmpm.unitLabel.metric")
        : i18n.t("sensorFields:runSpeedMmpm.unitLabel.us")
    }
    case "speedMmpm":
    case "speedObservedMmpm":
    case "speedReportsMmpm": {
      return isMetric
        ? i18n.t("sensorFields:speedMmpm.unitLabel.metric")
        : i18n.t("sensorFields:speedMmpm.unitLabel.us")
    }
    case "swathWidthMm": {
      return isMetric
        ? i18n.t("sensorFields:swathWidthMm.unitLabel.metric")
        : i18n.t("sensorFields:swathWidthMm.unitLabel.us")
    }
    case "voltageMv":
    case "voltageThresholdLowMv": {
      return i18n.t("sensorFields:voltageMv.unitLabel")
    }
    case "volumeLFinal":
    case "volumeLInitial":
    case "volumeLTotal": {
      return isMetric
        ? i18n.t("sensorFields:volumeLFinal.unitLabel.metric")
        : i18n.t("sensorFields:volumeLFinal.unitLabel.us")
    }
    case "widthMm": {
      return isMetric
        ? i18n.t("sensorFields:widthMm.unitLabel.metric")
        : i18n.t("sensorFields:widthMm.unitLabel.us")
    }
    case "normalThresholdM":
    case "highThresholdM":
    case "overThresholdM":
    case "thresholdBufferM": {
      return isMetric
        ? i18n.t(`sensorFields:thresholdBufferM.unitLabel.metric.${labelLength}`)
        : i18n.t(`sensorFields:thresholdBufferM.unitLabel.us.${labelLength}`)
    }
    case "applicationRateEstimatedMm":
    case "calibrationIntercept":
    case "calibrationSlope":
    case "configurationId":
    case "currentPercentage":
    case "deviceFunctionCall":
    case "i2cAddress":
    case "installedOrientation":
    case "ioPin":
    case "location":
    case "minTrackingPercentage":
    case "maxTrackingPercentage":
    case "nMagnets":
    case "nNozzles":
    case "normalThreshold":
    case "nSats":
    case "nWrapsOuterLayer":
    case "overThreshold":
    case "otaEnabled":
    case "pidDerivativeCoeff":
    case "pidGainCoeff":
    case "pidIntegralCoeff":
    case "pidSensorSetpoint":
    case "pidMeasurementType":
    case "rampSecondsRemaining":
    case "rampSecondsTotal":
    case "runMagnetCountCurrent":
    case "runMagnetCountMax":
    case "reportingPeriodEndDt":
    case "reportingPeriodStartDt":
    case "scheduledDeviceAction":
    case "sensorModelId":
    case "sensorPort":
    case "sensorPriority":
    case "sensorTriggeredUpdate":
    case "signalQuality":
    case "signalStrength":
    case "sprinklerType":
    case "startRampPercentage":
    case "stateCurrent":
    case "stateCurrentInitDt":
    case "statePrevious":
    case "statePreviousInitDt":
    case "switchType":
    case "targetRampPercentage":
    case "voltageChangeMvPerHour":
    case "voltageIntercept":
    case "voltageSlope":
    case "linearSlopeM": // { throw new Error('Not implemented yet: "linearSlopeM" case') }
    case "linearInterceptM": // { throw new Error('Not implemented yet: "linearInterceptM" case') }
    case "relativePercentage": // { throw new Error('Not implemented yet: "relativePercentage" case') }
    case "slope": // { throw new Error('Not implemented yet: "slope" case') }
    case "intercept": // { throw new Error('Not implemented yet: "intercept" case') }
    case "sensorPortMode":
    case "measurementType":
    case "gpsType": {
      break
    }
  }
  return ""

  // const label = i18n.t(`sensorFields:${fieldName}.unitLabel`)

  // if (typeof label === `string`) {
  //   return label
  // }
  // if (label) {
  //   // Can be a string or have properties 'short' and 'long'
  //   const result = label[measurementPreference]
  //   if (typeof result === "string") {
  //     return result
  //   }
  //   return result[labelLength]
  // }
  // return ``
}

export function useSensorUnitLabel() {
  const measurementPreference = useRootSelector(
    getUserMeasurementPreferenceFromState,
  )
  return (
    key: AnySensorKey,
    options?: {
      labelLength?: LabelLength
    },
  ): string => {
    return getSensorUnitLabel({
      fieldName: key,
      labelLength: options?.labelLength,
      measurementPreference,
    })
  }
}
